import { useApiQuery } from '@/hooks/useApiQuery';
import { apiGet, operationsClient } from '@/lib/api';
import type { AxiosInstance } from 'axios';
import type { QueryKey } from '@tanstack/react-query';

interface CreateListQueryHookConfig<TParams, TData, TRaw = TData> {
  /** Builds the React Query cache key from params. */
  queryKey: (params: TParams) => QueryKey;
  /** Returns the endpoint URL (no params). */
  endpoint: () => string;
  /** Axios client to use. Defaults to operationsClient. */
  client?: AxiosInstance;
  /**
   * Optional transform applied to the raw API response.
   * Use this when the response needs full reshaping before it matches TData.
   */
  transform?: (raw: TRaw) => TData;
  /**
   * When provided, the response field at this key is treated as an items array
   * and each element is passed through normalizeItem.
   * Requires normalizeItem to also be provided.
   */
  itemsKey?: keyof TData & string;
  /**
   * Per-item normalizer applied to each element in the itemsKey array.
   * Requires itemsKey to also be provided.
   */
  normalizeItem?: (raw: Record<string, unknown>) => unknown;
}

type SimpleOptions = { enabled?: boolean };

/**
 * Factory that produces a typed list-query hook following the project's
 * standard pattern: useApiQuery + apiGet + operationsClient.
 *
 * Supports three variants:
 *  1. Simple    — no transform, raw response == TData
 *  2. Transform — full response reshape via `transform`
 *  3. Map items — spread + remap one array field via `itemsKey` + `normalizeItem`
 */
export function createListQueryHook<TParams, TData, TRaw = TData>(
  config: CreateListQueryHookConfig<TParams, TData, TRaw>,
) {
  return function useListQuery(params: TParams = {} as TParams, options?: SimpleOptions) {
    const client = config.client ?? operationsClient;
    return useApiQuery<TData>({
      queryKey: config.queryKey(params),
      queryFn: async () => {
        if (config.transform) {
          const raw = await apiGet<TRaw>(client, config.endpoint(), {
            params: params as Record<string, unknown>,
          });
          return config.transform(raw);
        }

        if (config.itemsKey && config.normalizeItem) {
          const key = config.itemsKey;
          const normalize = config.normalizeItem;
          const res = await apiGet<TData>(client, config.endpoint(), {
            params: params as Record<string, unknown>,
          });
          const items = res[key];
          if (!Array.isArray(items)) return res;
          return {
            ...res,
            [key]: items.map((item) => normalize(item as Record<string, unknown>)),
          };
        }

        return apiGet<TData>(client, config.endpoint(), {
          params: params as Record<string, unknown>,
        });
      },
      showErrorToast: false,
      enabled: options?.enabled,
    });
  };
}
